import { Stack } from '@mui/material'
import { useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import pxToRem from '../../../../helpers/pxToRem'
import styles from '../index.module.scss'
import { RegModalWrapper } from '../ModalContent'
import { v4 as uuidv4 } from 'uuid'
import {
    FileUpload,
    UploadedFileList,
} from '../../../../components/FileUpload/FileUploadV2'
import { documentUploadHandler_v2 } from './helper'
import { setErrorMsg } from '../../../../redux/uiSlice'
import z from 'zod'
import { FileUploadSchema } from '../../../../schemas/helper'
import {
    selectTeacherLatestService,
    selectTeacherSetLocation,
} from '../../../../redux/teacherSlice'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import {
    FieldState_v2,
    serviceBandTypes,
    TeacherService,
} from '../../../../schemas/teacherReg'
import { selectUserUid } from '../../../../redux/userSlice'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { push, ref, update } from 'firebase/database'
import { db } from '../../../../firebase/configs'
import dayjs from '../../../../helpers/dayjs'
import { logger } from '../../../../helpers/logger'
import FormSelect from '../../../../components/FormComponents/FormSelect'

const tempFileSchema = z.object({
    id: z.string().optional(),
    docTitle: z.string(),
    value: FileUploadSchema,
})

const formSchema = z.discriminatedUnion('band', [
    z.object({
        band: serviceBandTypes.extract(['band1', "I'm Unsure"]),
        documents: z.array(tempFileSchema),
    }),
    z.object({
        band: serviceBandTypes.extract(['band2', 'band3']),
        documents: z.array(tempFileSchema).min(1),
    }),
])
type FormType = z.infer<typeof formSchema>

export const AccreditationLevelWrapper = () => {
    const service = useSelector(selectTeacherLatestService, shallowEqual)
    const homeState = useSelector(selectTeacherSetLocation)

    if (service === undefined || homeState.status !== 'success')
        return <LoadingSpinner />

    return (
        <AccreditationLevel
            service={service}
            homeState={homeState.data.homeState}
        />
    )
}

const AccreditationLevel = ({
    service,
    homeState,
}: {
    service: [string, TeacherService] | null
    homeState: string
}) => {
    const dispatch = useDispatch()
    const userId = useSelector(selectUserUid)

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { isDirty, isValid },
    } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            band: service?.[1].userLevelNomination || undefined,
            documents: Object.entries(service?.[1].urls || {}).map(
                ([key, el]) => ({
                    id: key,
                    docTitle: el.docTitle,
                    value: el.value,
                })
            ),
        },
    })

    // const [files, setFiles] = useState<MultiFileType[]>(
    //     service?.[1].urls ? getServiceDocs(service[1].urls) : []
    // )

    const { append, remove } = useFieldArray({
        control,
        name: 'documents',
    })

    const selectedBand = watch('band')

    const handleUpdate = async (data: FormType) => {
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }
            if (!isDirty) return

            const newDocs: { docTitle: string; type: string; value: string }[] =
                []
            // Handle document uploads
            if (data.documents) {
                const docsToUpload: File[] = []
                Object.entries(data.documents).map(([_, v]) => {
                    if (typeof v.value !== 'string') {
                        docsToUpload.push(v.value)
                    }
                })

                if (docsToUpload.length > 0) {
                    const res = await Promise.allSettled(
                        docsToUpload.map(async (v) =>
                            documentUploadHandler_v2(userId, v)
                        )
                    )
                    res.forEach((r, i) => {
                        if (r.status === 'fulfilled') {
                            newDocs.push({
                                docTitle: r.value.docTitle,
                                type: r.value.fileType,
                                value: r.value.fileUrl,
                            })
                        } else {
                            logger.warn(r.reason)
                        }
                    })
                }
            }

            let toUpdate = {
                timeUploaded: dayjs().unix(),
                uploadedBy: userId,
                userLevelNomination: data.band,
                userLocation: homeState,
                // urls,
            }

            if (typeof service?.[0] === 'string') {
                await update(
                    ref(db, `teachers/${userId}/service/${service[0]}`),
                    toUpdate
                )

                // Add new documents
                if (newDocs.length > 0) {
                    newDocs.map((doc) =>
                        push(
                            ref(
                                db,
                                `teachers/${userId}/service/${service[0]}/urls`
                            ),
                            doc
                        )
                    )
                }

                // Delete removed documents
                const docToRemove = Object.keys(service[1].urls || {})
                    .filter((id) => !data.documents.some((el) => el.id === id))
                    .reduce((a, c) => ({ ...a, [c]: null }), {})

                if (Object.keys(docToRemove).length > 0) {
                    await update(
                        ref(
                            db,
                            `teachers/${userId}/service/${service[0]}/urls`
                        ),
                        docToRemove
                    )
                }
            } else {
                // Add new service object
                await push(ref(db, `teachers/${userId}/service`), {
                    ...toUpdate,
                    urls: newDocs.reduce(
                        (a, c) => ({ ...a, [uuidv4()]: c }),
                        {} as Record<string, any>
                    ),
                })
            }
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    const renderText = useMemo(() => {
        switch (selectedBand) {
            case 'band1':
                return <>I have Provisional teaching accreditation from NESA.</>
            case 'band2':
                return (
                    <>
                        I am an accredited teacher who has:
                        <br />a{')'} Proficient Accreditation, and <br />b{')'}{' '}
                        Completed 2 years {'('}406 days{')'} of full-time
                        equivalent {'('}FTE{')'} experience.
                    </>
                )
            case 'band3':
                return (
                    <>
                        I am an accredited teacher who has:
                        <br />a{')'} Proficient Accreditation, and <br />b{')'}{' '}
                        Completed more than 2 years {'('}406 days{')'} of
                        full-time equivalent {'('}FTE{')'} experience at the
                        Proficient level.
                    </>
                )
            case "I'm Unsure":
                return (
                    <>
                        I am unsure which band I belong to and would like some
                        guidance on the requirements and qualifications needed
                        to determine my appropriate level as a teacher.
                    </>
                )
            default:
                return <></>
        }
    }, [selectedBand])

    const getLabel = (level: z.infer<typeof serviceBandTypes>) => {
        switch (level) {
            case 'band1':
                return 'Band 1 (Provisional Teacher)'
            case 'band2':
                return 'Band 2 (Proficient Teacher - more than 2 years FTE)'
            case 'band3':
                return 'Band 3 (Proficient Teacher - more than 2 years FTE at the proficient level)'
            case "I'm Unsure":
                return "I'm Unsure"
            default:
                return ''
            // return <></>
        }
    }
    const state: FieldState_v2 = service !== null ? 'submitted' : 'incomplete'

    return (
        <>
            <RegModalWrapper
                title="Accreditation Level"
                handleUpdate={handleSubmit(handleUpdate)}
                state={state}
                isAllComplete={isValid}
            >
                <Stack gap={pxToRem(40)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>Accreditation Level</h5>
                        <p className={styles.p}>
                            To ensure you are paid correctly, nominate the band
                            based on your years of experience:
                        </p>
                    </Stack>
                    <Stack gap={pxToRem(24)}>
                        <FormSelect
                            control={control}
                            name="band"
                            options={serviceBandTypes.options.map((el) => ({
                                label: getLabel(el),
                                value: el,
                            }))}
                            props={{
                                label: 'Casual Teacher Band',
                                MenuProps: {
                                    PaperProps: {
                                        style: { overflowX: 'scroll' },
                                    },
                                },
                            }}
                        />
                        <Stack
                            className={styles.accreditationLevelBanner}
                            sx={{
                                opacity: selectedBand !== undefined ? 1 : 0,
                                transition: 'all 300ms',
                            }}
                        >
                            <small> {renderText} </small>
                        </Stack>

                        {/* 
                        <TeacherRegFormControl variant="standard">
                            <InputLabel>Casual Teacher Band</InputLabel>
                            <Select
                                value={selected}
                                onChange={(e) =>
                                    setSelected(
                                        e.target.value as NSWAccreditationLevel
                                    )
                                }
                                MenuProps={{
                                    PaperProps: {
                                        style: { overflowX: 'scroll' },
                                    },
                                }}
                            >
                                {Object.values(NSWAccreditationLevel)
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((el, i) => (
                                        <MenuItem key={i} value={el}>
                                            {getLabel(el)}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </TeacherRegFormControl> */}

                        {selectedBand !== undefined && (
                            <Controller
                                control={control}
                                name="documents"
                                render={(props) => (
                                    <>
                                        <FileUpload
                                            multiple
                                            value={props.field.value}
                                            append={append}
                                            extensions={'document'}
                                            label={
                                                <small>{`Upload statement of service ${['band1', "I'm Unsure"].includes(selectedBand) ? '(Optional)' : '(Required)'}`}</small>
                                            }
                                        />
                                        <UploadedFileList
                                            files={props.field.value}
                                            remove={(i) => remove(i)}
                                        />
                                    </>
                                )}
                            />
                        )}
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export default AccreditationLevelWrapper
